import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "./AuthProvider";
import { supabase } from "../lib/supabase";

// Invitation emails link to /login#invite=<email>. The fragment keeps the
// address out of server and CDN logs while letting the page prefill it.
function invitedEmailFromHash() {
  const match = window.location.hash.match(/^#invite=(.+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]).trim().toLowerCase();
  } catch {
    return match[1].trim().toLowerCase();
  }
}

export function LoginPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invited] = useState(invitedEmailFromHash);
  const [mode, setMode] = useState<"login" | "signup">(searchParams.get("mode") === "signup" && !invited ? "signup" : "login");
  const [email, setEmail] = useState(invited);
  const [password, setPassword] = useState("");
  // Recovery falls back to a one-time sign-in code for people who forgot
  // their password (and for invited teammates who never set one).
  const [recovery, setRecovery] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [token, setToken] = useState("");
  const [sent, setSent] = useState(Boolean(invited));
  const [hasCode, setHasCode] = useState(Boolean(invited));
  useEffect(() => {
    if (invited) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [invited]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [bootstrapRetry, setBootstrapRetry] = useState(false);

  if (session && !busy && !bootstrapRetry) return <Navigate to="/app" replace />;

  const switchMode = (nextMode: "login" | "signup") => {
    setMode(nextMode);
    setSearchParams(nextMode === "signup" ? { mode: "signup" } : {});
    setSent(false);
    setHasCode(false);
    setToken("");
    setError("");
    setNotice("");
    setRecovery(false);
    setBootstrapRetry(false);
  };

  const bootstrapAccount = async (accessToken: string) => {
    const response = await fetch("/api/ads/account-bootstrap", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    const payload = await response.json().catch(() => ({})) as { message?: string };
    if (!response.ok) throw new Error(payload.message || "Your advertising account could not be created.");
  };

  const sendCode = async () => {
    if (!supabase) throw new Error("Authentication is not configured yet.");
    const normalized = email.trim().toLowerCase();
    if (!normalized) throw new Error("Enter your email address.");
    if (mode === "signup" && !businessName.trim()) throw new Error("Enter your business name.");
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        shouldCreateUser: mode === "signup",
        ...(mode === "signup" ? { data: { business_name: businessName.trim() } } : {}),
      },
    });
    if (sendError) {
      // Supabase rejects sign-in for unknown emails with "Signups not allowed
      // for otp". Instead of surfacing that dead end, carry the email into the
      // signup flow so the person can create their account in one step.
      if (mode === "login" && /signups? not allowed/i.test(sendError.message || "")) {
        setMode("signup");
        setSearchParams({ mode: "signup" });
        setEmail(normalized);
        setSent(false);
        setHasCode(false);
        setToken("");
        setNotice("No account exists for this email yet. Add your business name and we’ll create your workspace.");
        return;
      }
      throw sendError;
    }
    setEmail(normalized);
    setSent(true);
    setHasCode(false);
    setNotice(`${mode === "signup" ? "An account verification" : "A secure sign-in"} code was sent. Only the newest code will work.`);
  };

  const signInWithPassword = async () => {
    if (!supabase) throw new Error("Authentication is not configured yet.");
    const normalized = email.trim().toLowerCase();
    if (!normalized) throw new Error("Enter your email address.");
    if (!password) throw new Error("Enter your password.");
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: normalized, password });
    if (signInError) {
      // Accounts created before verifying their email still need the code.
      if (/email not confirmed/i.test(signInError.message || "")) {
        await supabase.auth.resend({ type: "signup", email: normalized });
        setEmail(normalized);
        setSent(true);
        setHasCode(false);
        setNotice("Verify your email first — we just sent a fresh 6-digit code.");
        return;
      }
      throw signInError;
    }
    navigate("/app", { replace: true });
  };

  const signUpWithPassword = async () => {
    if (!supabase) throw new Error("Authentication is not configured yet.");
    const normalized = email.trim().toLowerCase();
    if (!normalized) throw new Error("Enter your email address.");
    if (!businessName.trim()) throw new Error("Enter your business name.");
    if (password.length < 8) throw new Error("Choose a password with at least 8 characters.");
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: normalized,
      password,
      options: { data: { business_name: businessName.trim() } },
    });
    if (signUpError) throw signUpError;
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setMode("login");
      setNotice("An account already exists for this email. Sign in instead.");
      return;
    }
    setEmail(normalized);
    setSent(true);
    setHasCode(false);
    setNotice("We emailed you a 6-digit code to verify your email.");
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    let emailVerified = false;
    try {
      if (!sent) {
        if (recovery) await sendCode();
        else if (mode === "signup") await signUpWithPassword();
        else await signInWithPassword();
      } else {
        if (!supabase) throw new Error("Authentication is not configured yet.");
        if (token.length !== 6) throw new Error("Enter the 6-digit code.");
        // Signup confirmations carry a signup-type code and invitation emails
        // an invite-type code, so when the regular verification rejects the
        // token, retry the same code against those types.
        const emailAttempt = await supabase.auth.verifyOtp({ email, token, type: "email" });
        let data = emailAttempt.data;
        if (emailAttempt.error) {
          const signupAttempt = await supabase.auth.verifyOtp({ email, token, type: "signup" });
          if (!signupAttempt.error) {
            data = signupAttempt.data;
          } else {
            const invite = await supabase.auth.verifyOtp({ email, token, type: "invite" });
            if (invite.error) throw emailAttempt.error;
            data = invite.data;
          }
        }
        if (!data.session?.access_token) throw new Error("Your email was verified, but no session was created. Request a new code.");
        emailVerified = true;
        await bootstrapAccount(data.session.access_token);
        if (recovery) {
          // The code signed them in; now let them replace the password they
          // forgot before they continue.
          setSent(false);
          setResetPassword(true);
        } else {
          navigate("/app", { replace: true });
        }
      }
    } catch (caught) {
      if (emailVerified) setBootstrapRetry(true);
      setError(caught instanceof Error ? caught.message : "Sign-in could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const saveNewPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) return;
    if (newPassword.length < 8) { setError("Choose a password with at least 8 characters."); return; }
    setBusy(true);
    setError("");
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      navigate("/app", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The password could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  const retryBootstrap = async () => {
    if (!session?.access_token) return;
    setBusy(true);
    setError("");
    try {
      await bootstrapAccount(session.access_token);
      setBootstrapRetry(false);
      navigate("/app", { replace: true });
    } catch (caught) {
      setBootstrapRetry(true);
      setError(caught instanceof Error ? caught.message : "Your advertising account could not be created.");
    } finally { setBusy(false); }
  };

  const useExistingCode = () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError("Enter your email address first.");
      return;
    }
    setEmail(normalized);
    setHasCode(true);
    setSent(true);
    setError("");
    setNotice("");
  };

  const resend = async () => {
    setBusy(true);
    setError("");
    try {
      await sendCode();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "A new code could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-story">
        <Logo inverse />
        <div className="login-story__copy">
          <span className="eyebrow eyebrow--light">OXWELL</span>
          <h1>Your advertising accounts, <em>funded and in focus.</em></h1>
          <p className="login-story__sub">One portal for spend and campaign performance across every platform Oxwell manages for you.</p>
          <div className="login-proof">
            <span><CheckCircle2 /> Campaigns and reporting in one place</span>
            <span><CheckCircle2 /> Billing and pages in one workflow</span>
            <span><CheckCircle2 /> Hands-on support from your platform</span>
          </div>
        </div>
        <p><a href="mailto:hello@oxwell.io">hello@oxwell.io</a></p>
      </section>
      <section className="login-panel">
        <div className="login-card">
          {!sent && !bootstrapRetry && !resetPassword && <div className="auth-mode" role="tablist" aria-label="Account access">
            <button role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} type="button" onClick={() => switchMode("login")}>Sign in</button>
            <button role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "is-active" : ""} type="button" onClick={() => switchMode("signup")}>Create account</button>
          </div>}
          {sent && !bootstrapRetry && <div className="code-header">
            <button type="button" onClick={() => { setSent(false); setHasCode(false); setToken(""); setNotice(""); }} aria-label="Back to email entry"><ArrowLeft /><span>{email}</span></button>
            <button type="button" onClick={() => { setSent(false); setHasCode(false); setToken(""); setNotice(""); }}>Change</button>
          </div>}
          <h2>{resetPassword ? "Set a new password" : bootstrapRetry ? "Finish account setup" : sent ? "Enter your 6-digit code" : recovery ? "Get a sign-in code" : mode === "signup" ? "Create your client account" : "Sign in to Oxwell"}</h2>
          <p>{resetPassword ? "You’re signed in. Choose the password you’ll use from now on." : bootstrapRetry ? "Your email is verified. Retry the secure ad-account connection to continue." : sent ? (hasCode ? <>Enter the six-digit code from your email. The code expires in 10 minutes.</> : <>We sent it to <strong>{email}</strong> a moment ago. The code expires in 10 minutes.</>) : recovery ? "We’ll email you a one-time 6-digit code that signs you in, then you can set a new password." : mode === "signup" ? "Set your email and password, then verify your email with a 6-digit code." : "Sign in with your work email and password."}</p>
          {error && <div className="form-alert" role="alert">{error}</div>}
          {notice && <div className="form-alert form-alert--success">{notice}</div>}
          {resetPassword ? <form onSubmit={saveNewPassword}>
            <label><span>New password</span><input value={newPassword} onChange={(event) => { setNewPassword(event.target.value); setError(""); }} placeholder="At least 8 characters" type="password" autoComplete="new-password" minLength={8} required autoFocus /></label>
            <button className="button button--primary button--wide" type="submit" disabled={busy || newPassword.length < 8}>{busy ? "Saving…" : "Save password and continue"}</button>
            <div className="login-actions"><button type="button" onClick={() => navigate("/app", { replace: true })} disabled={busy}>Skip for now</button></div>
          </form> : bootstrapRetry ? <button className="button button--primary button--wide" type="button" onClick={() => void retryBootstrap()} disabled={busy}>{busy ? "Creating account…" : "Retry account setup"}</button> : <form onSubmit={submit}>
            {!sent ? (
              <>
                {mode === "signup" && !recovery && <label><span>Business name</span><input value={businessName} onChange={(event) => setBusinessName(event.target.value)} placeholder="Your company" autoComplete="organization" required /></label>}
                <label><span>Work email</span><input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" type="email" autoComplete="email" required autoFocus /></label>
                {!recovery && <label><span>Password</span><input value={password} onChange={(event) => setPassword(event.target.value)} placeholder={mode === "signup" ? "At least 8 characters" : "Your password"} type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} minLength={mode === "signup" ? 8 : undefined} required /></label>}
                {mode === "login" && !recovery && <div className="login-forgot"><button type="button" onClick={() => { setRecovery(true); setError(""); setNotice(""); }}>Forgot password?</button></div>}
              </>
            ) : (
              <label><span>One-time code</span><input className="otp-input" value={token} onChange={(event) => setToken(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" autoComplete="one-time-code" required autoFocus /></label>
            )}
            <button className="button button--primary button--wide" type="submit" disabled={busy || (sent && token.length !== 6)}>{busy ? (sent ? mode === "signup" ? "Creating account…" : "Signing in…" : "Sending code…") : sent ? (mode === "signup" ? "Verify and create account" : "Verify and continue") : recovery ? "Email me a sign-in code" : mode === "signup" ? "Create account and verify email" : "Sign in"}{!busy && <ArrowRight />}</button>
          </form>}
          {!sent && !bootstrapRetry && !resetPassword && recovery && <p className="login-hint">Codes expire after 10 minutes and can be used once.</p>}
          {!sent && !bootstrapRetry && !resetPassword && mode === "login" && <div className="login-actions">{recovery && <button type="button" onClick={() => { setRecovery(false); setError(""); setNotice(""); }} disabled={busy}>Back to password sign-in</button>}<button type="button" onClick={useExistingCode} disabled={busy}>Already have a code? Enter it</button></div>}
          {sent && <div className="login-actions login-actions--code"><span>Didn’t get the code?</span><button type="button" onClick={resend} disabled={busy}>{hasCode ? "Email me a fresh code" : "Resend code"}</button></div>}
          {sent && <p className="login-switch">Still stuck? <a href="mailto:hello@oxwell.io">Contact support</a></p>}
          {!sent && !bootstrapRetry && !resetPassword && (mode === "login"
            ? <p className="login-switch">New to Oxwell? <button type="button" onClick={() => switchMode("signup")}>Create an account</button></p>
            : <p className="login-switch">Already have an account? <button type="button" onClick={() => switchMode("login")}>Sign in</button></p>)}
          {!sent && <p className="login-legal">By continuing, you agree to the platform’s <Link to="/terms">Ads Terms</Link> and acknowledge its privacy policy.</p>}
        </div>
      </section>
    </main>
  );
}
